import { Link } from 'react-router-dom';
import ShelfFilters, { useShelfFilter } from './ShelfFilters';

/**
 * The grid of activated books, shared by the student dashboard and My Library so the
 * two cannot drift apart.
 *
 * <p>Cover, title, class, subject and the expiry indicator are all Module 6's data
 * fields; they come from the server rather than being assembled here, so the badge a
 * student sees always agrees with the window the server would enforce.
 *
 * <p>Subject and class filter it, the same control the Content Library uses — a
 * student with a book for every subject is looking for one of them, and by the time
 * that is worth doing it is the same job the back office does on the catalogue. It is
 * inside the shelf rather than on the two pages that render one, so the dashboard and
 * My Library cannot end up with different ideas of how a shelf is filtered; and it
 * hides itself on a shelf small enough that the chips would be longer than the books.
 */
export default function BookShelf({ books }) {
  const { shelf, clearAll, filterProps } = useShelfFilter(books);

  return (
    <>
      <ShelfFilters {...filterProps} />

      {shelf.filtered && shelf.matches.length === 0 && (
        <div className="card empty">
          No book in your library matches this.{' '}
          <button type="button" className="link-button" onClick={clearAll}>
            Clear it
          </button>
          .
        </div>
      )}

      <section className="shelf">
        {shelf.matches.map((book) => (
          <BookCard key={book.titleId} book={book} />
        ))}
      </section>
    </>
  );
}

export function BookCard({ book }) {
  // An expired book stays on the shelf but does not open: the server would refuse it,
  // and offering the link anyway would only produce an error the student cannot act on.
  const inner = <BookCover book={book} />;

  return (
    <article className={`book${book.accessible ? '' : ' book-expired'}`}>
      {book.accessible ? (
        <Link
          to={`/library/${book.titleId}`}
          className="book-cover jacket"
          aria-label={`Open ${book.name}`}
        >
          {inner}
        </Link>
      ) : (
        <div className="book-cover jacket" aria-hidden="true">{inner}</div>
      )}
      <div className="book-body">
        <b>
          {book.accessible ? <Link to={`/library/${book.titleId}`}>{book.name}</Link> : book.name}
        </b>
        <span className="muted small">
          {/* Printed as stored, not prefixed. A title's class *is* the string —
              MasterCatalogue.BOOK_CLASSES holds "Class 8", not "8" — so adding the
              word put "Class Class 8" on every book that had one. Every other screen
              prints it raw; this was the one that did not. */}
          {[book.subject, book.classLevel].filter(Boolean).join(' · ') || '—'}
        </span>
        <div className="book-foot">
          <span className="tiny muted">Added {formatDate(book.activatedAt)}</span>
          <ExpiryBadge book={book} />
        </div>
      </div>
    </article>
  );
}

/**
 * The jacket from the Title master, or the book's initials when it has none. The
 * fallback is deliberate rather than a placeholder image: a catalogue is loaded over
 * time, and a shelf of identical grey rectangles is worse than a shelf of readable
 * initials.
 *
 * <p>The frame is portrait and the image is contained, the same as the catalogue's.
 * It used to be a landscape frame filled by cropping, which trimmed the top and
 * bottom off every jacket that was actually uploaded — including the title printed
 * across it. A shelf exists so a book can be recognised, and the cover is the whole
 * of how that happens here: the name under it is the only other thing on the card.
 */
function BookCover({ book }) {
  if (!book.coverUrl) {
    return <span className="book-initials">{initialsOf(book.shortName || book.name)}</span>;
  }
  return <img src={book.coverUrl} alt="" loading="lazy" />;
}

/**
 * The expiry indicator the design asks for. It reads in days near the end, because
 * "23 days left" prompts a student to ask for a renewal and a date does not.
 */
export function ExpiryBadge({ book }) {
  if (!book.accessible) {
    return <span className="badge badge-danger">Expired</span>;
  }
  if (book.accessStatus === 'RENEWAL_DUE') {
    return <span className="badge badge-warn">{book.daysRemaining} days left</span>;
  }
  // Month and year: the exact day does not help at a glance, and a full date wraps
  // inside the card. The precise date is on the confirmation banner.
  return <span className="badge badge-ok">Until {formatMonth(book.expiresAt)}</span>;
}

export function initialsOf(text) {
  return String(text ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
}

export function formatDate(value) {
  return value ? new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—';
}

export function formatMonth(value) {
  return value
    ? new Date(value).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
    : '—';
}
